import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Copy, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useArkemaLang } from "@/components/thales/i18n";
import { trackPostHogEvent } from "@/lib/analytics";

const EMAIL = "celia.desailly@naboo.app";
const OMBELINE_PHOTO = "/lovable-uploads/celia-desailly.png";

interface OmbelineContactPopoverProps {
  children: React.ReactNode;
  align?: "start" | "center" | "end";
}

const OmbelineContactPopover: React.FC<OmbelineContactPopoverProps> = ({
  children,
  align = "end",
}) => {
  const { t, lang } = useArkemaLang();
  const trackContact = (method: string) =>
    trackPostHogEvent("aircall_contact_celia_clicked", {
      page: "aircall",
      location: "popover",
      method,
      lang,
    });
  const SUBJECT = t("pop.subject");

  const GMAIL_URL = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
    EMAIL,
  )}&su=${encodeURIComponent(SUBJECT)}`;
  const OUTLOOK_URL = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(
    EMAIL,
  )}&subject=${encodeURIComponent(SUBJECT)}`;

  const copyEmail = async () => {
    trackContact("copy");
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(EMAIL);
      } else {
        const ta = document.createElement("textarea");
        ta.value = EMAIL;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(t("pop.copied"), { description: EMAIL });
    } catch {
      toast.error(t("pop.copy_error"), { description: EMAIL });
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={8}
        className="w-[320px] p-0 rounded-2xl border border-black/10 shadow-xl bg-white"
        style={{ zIndex: 100000 }}
      >
        <div className="flex items-center gap-3 p-4 border-b border-black/5">
          <img
            src={OMBELINE_PHOTO}
            alt="Célia"
            className="w-11 h-11 rounded-full object-cover"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#111111] truncate">
              Célia
            </p>
            <p className="text-xs text-[#6b7280] truncate">{EMAIL}</p>
          </div>
        </div>

        <div className="p-3 space-y-2">
          <button
            type="button"
            onClick={copyEmail}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#C6E278] text-[#111111] text-sm font-semibold px-3 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
          >
            <Copy className="w-4 h-4" />
            {t("pop.copy")}
          </button>

          <a
            href={GMAIL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackContact("gmail")}
            className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#111111] text-sm font-semibold px-3 py-2.5 rounded-xl border border-black/15 hover:border-black/40 hover:bg-black/5 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            {t("pop.gmail")}
          </a>

          <a
            href={OUTLOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackContact("outlook")}
            className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#111111] text-sm font-semibold px-3 py-2.5 rounded-xl border border-black/15 hover:border-black/40 hover:bg-black/5 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            {t("pop.outlook")}
          </a>

          <a
            href={`mailto:${EMAIL}?subject=${encodeURIComponent(SUBJECT)}`}
            onClick={() => trackContact("mailto")}
            className="w-full inline-flex items-center justify-center gap-2 text-xs text-[#6b7280] hover:text-[#111111] py-2 transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            {t("pop.mail_app")}
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default OmbelineContactPopover;
